import { useCallback, useEffect, useRef, useState } from 'react';
import { PhotoService } from '../services';

type FeedbackVariant = 'error' | 'success' | 'warning';

interface ProfilePhotoFeedback {
  message: string;
  variant: FeedbackVariant;
}

interface UseProfilePhotoUploadParams {
  studentId?: number | null;
  userId?: string | null;
}

interface UseProfilePhotoUploadResult {
  displayUrl: string | null;
  hasPendingUpload: boolean;
  isUploading: boolean;
  feedback: ProfilePhotoFeedback | null;
  selectFile: (file: File) => Promise<void>;
  uploadPendingPhoto: (nextStudentId?: number | null) => Promise<string | null>;
  clearFeedback: () => void;
}

const MAX_PROFILE_FILE_SIZE = 15 * 1024 * 1024;
const PROFILE_OUTPUT_SIZE = 512;
const PROFILE_OUTPUT_QUALITY = 0.85;
const ACCEPTED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

const buildUploadError = (fallbackMessage: string, error: unknown) => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallbackMessage;
};

const loadImageElement = (src: string) => new Promise<HTMLImageElement>((resolve, reject) => {
  const image = new Image();

  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('Não foi possível processar a imagem selecionada.'));
  image.src = src;
});

const canvasToBlob = (canvas: HTMLCanvasElement) => new Promise<Blob>((resolve, reject) => {
  canvas.toBlob((blob) => {
    if (!blob) {
      reject(new Error('Não foi possível preparar a imagem para envio.'));
      return;
    }

    resolve(blob);
  }, 'image/jpeg', PROFILE_OUTPUT_QUALITY);
});

const normalizeFileName = (fileName: string) => {
  const baseName = fileName.replace(/\.[^.]+$/, '').trim() || 'profile-picture';
  return `${baseName}.jpg`;
};

const processProfilePhotoFile = async (file: File): Promise<File> => {
  if (!ACCEPTED_IMAGE_TYPES.has(file.type)) {
    throw new Error('Envie uma imagem em JPG, PNG ou WEBP.');
  }

  if (file.size > MAX_PROFILE_FILE_SIZE) {
    throw new Error('A imagem deve ter no máximo 15 MB.');
  }

  const sourceUrl = URL.createObjectURL(file);

  try {
    const image = await loadImageElement(sourceUrl);
    const shortestSide = Math.min(image.naturalWidth, image.naturalHeight);

    if (!shortestSide) {
      throw new Error('Não foi possível processar a imagem selecionada.');
    }

    const sourceX = (image.naturalWidth - shortestSide) / 2;
    const sourceY = (image.naturalHeight - shortestSide) / 2;
    const canvas = document.createElement('canvas');
    canvas.width = PROFILE_OUTPUT_SIZE;
    canvas.height = PROFILE_OUTPUT_SIZE;

    const context = canvas.getContext('2d');

    if (!context) {
      throw new Error('Não foi possível processar a imagem selecionada.');
    }

    context.drawImage(
      image,
      sourceX,
      sourceY,
      shortestSide,
      shortestSide,
      0,
      0,
      PROFILE_OUTPUT_SIZE,
      PROFILE_OUTPUT_SIZE,
    );

    const blob = await canvasToBlob(canvas);

    return new File([blob], normalizeFileName(file.name), {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } finally {
    URL.revokeObjectURL(sourceUrl);
  }
};

const useProfilePhotoUpload = ({ studentId, userId }: UseProfilePhotoUploadParams): UseProfilePhotoUploadResult => {
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [feedback, setFeedback] = useState<ProfilePhotoFeedback | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  const replacePreviewUrl = useCallback((nextPreviewUrl: string | null) => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
    }

    previewUrlRef.current = nextPreviewUrl;
    setPreviewUrl(nextPreviewUrl);
  }, []);

  useEffect(() => () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
    }
  }, []);

  useEffect(() => {
    if (studentId) {
      let cancelled = false;

      const loadProfilePhoto = async () => {
        try {
          const nextPhotoUrl = await PhotoService.getProfilePictureUrl(studentId, true, userId);

          if (!cancelled) {
            setPhotoUrl(nextPhotoUrl);
          }
        } catch {
          if (!cancelled) {
            setPhotoUrl(null);
          }
        }
      };

      loadProfilePhoto();

      return () => {
        cancelled = true;
      };
    }

    if (!userId) {
      setPhotoUrl(null);
      return;
    }

    let cancelled = false;

    const loadProfilePhoto = async () => {
      try {
        const nextPhotoUrl = await PhotoService.getPortalUserProfilePictureUrl(userId, true);

        if (!cancelled) {
          setPhotoUrl(nextPhotoUrl);
        }
      } catch {
        if (!cancelled) {
          setPhotoUrl(null);
        }
      }
    };

    loadProfilePhoto();

    return () => {
      cancelled = true;
    };
  }, [studentId, userId]);

  const uploadStudentFile = useCallback(async (targetStudentId: number, file: File, shouldThrow = false) => {
    setIsUploading(true);
    setFeedback(null);

    try {
      const uploadedUrl = await PhotoService.uploadProfilePicture(targetStudentId, file);
      setPhotoUrl(uploadedUrl);
      setPendingFile(null);
      replacePreviewUrl(null);
      setFeedback({ message: 'Foto de perfil atualizada com sucesso.', variant: 'success' });
      return uploadedUrl;
    } catch (error) {
      const message = buildUploadError('Não foi possível enviar a foto de perfil.', error);
      setFeedback({ message, variant: 'error' });

      if (shouldThrow) {
        throw error instanceof Error ? error : new Error(message);
      }

      return null;
    } finally {
      setIsUploading(false);
    }
  }, [replacePreviewUrl]);

  const uploadPortalUserFile = useCallback(async (targetUserId: string, file: File, shouldThrow = false) => {
    setIsUploading(true);
    setFeedback(null);

    try {
      const uploadedUrl = await PhotoService.uploadPortalUserProfilePicture(targetUserId, file);
      setPhotoUrl(uploadedUrl);
      setPendingFile(null);
      replacePreviewUrl(null);
      setFeedback({ message: 'Foto de perfil atualizada com sucesso.', variant: 'success' });
      return uploadedUrl;
    } catch (error) {
      const message = buildUploadError('Não foi possível enviar a foto de perfil.', error);
      setFeedback({ message, variant: 'error' });

      if (shouldThrow) {
        throw error instanceof Error ? error : new Error(message);
      }

      return null;
    } finally {
      setIsUploading(false);
    }
  }, [replacePreviewUrl]);

  const selectFile = useCallback(async (file: File) => {
    try {
      const processedFile = await processProfilePhotoFile(file);
      const nextPreviewUrl = URL.createObjectURL(processedFile);

      replacePreviewUrl(nextPreviewUrl);
      setPendingFile(processedFile);
      setFeedback(null);

      if (studentId) {
        await uploadStudentFile(studentId, processedFile);
        return;
      }

      if (userId) {
        await uploadPortalUserFile(userId, processedFile);
      }
    } catch (error) {
      setFeedback({
        message: buildUploadError('Não foi possível preparar a imagem selecionada.', error),
        variant: 'error',
      });
    }
  }, [replacePreviewUrl, uploadPortalUserFile, uploadStudentFile, studentId, userId]);

  const uploadPendingPhoto = useCallback(async (nextStudentId?: number | null) => {
    if (!pendingFile) {
      return null;
    }

    if (studentId != null || nextStudentId != null) {
      const resolvedStudentId = nextStudentId ?? studentId;

      if (!resolvedStudentId) {
        const error = new Error('Salve os dados da aluna antes de concluir o envio da foto de perfil.');
        setFeedback({ message: error.message, variant: 'warning' });
        throw error;
      }

      return uploadStudentFile(resolvedStudentId, pendingFile, true);
    }

    if (!userId) {
      const error = new Error('Usuário não identificado para envio da foto de perfil.');
      setFeedback({ message: error.message, variant: 'warning' });
      throw error;
    }

    return uploadPortalUserFile(userId, pendingFile, true);
  }, [pendingFile, uploadPortalUserFile, uploadStudentFile, studentId, userId]);

  const clearFeedback = useCallback(() => {
    setFeedback(null);
  }, []);

  return {
    displayUrl: previewUrl ?? photoUrl,
    hasPendingUpload: Boolean(pendingFile),
    isUploading,
    feedback,
    selectFile,
    uploadPendingPhoto,
    clearFeedback,
  };
};

export default useProfilePhotoUpload;
